import React, { ReactNode } from 'react';
import { ArrowForward } from '@mui/icons-material';
import { Typography, Stack, Chip, Box } from '@mui/material';
import { BpfChangeType } from '../../api';

interface BeforeAfter {
  before: unknown;
  after: unknown;
}

const getNameOrValue = (val: unknown): string => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object' && 'name' in val) return (val as { name: string }).name;
  if (typeof val === 'object' && 'label' in val) return (val as { label: string }).label;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

const ValueChip = ({
  children,
  color = 'default',
}: {
  children: ReactNode;
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}) => (
  <Chip size="small" color={color} variant="outlined" label={<Typography variant="body2">{children}</Typography>} />
);

const ChangeArrow = () => <ArrowForward sx={{ fontSize: 16, color: 'text.secondary', mx: 0.5 }} />;

const Label = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" color="text.secondary" fontWeight={500}>
    {children}
  </Typography>
);

const BeforeAfterRow = ({
  label,
  before,
  after,
  afterColor = 'primary',
}: {
  label?: string;
  before: string;
  after: string;
  afterColor?: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}) => (
  <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
    {label && <Label>{label}:</Label>}
    <ValueChip>{before}</ValueChip>
    <ChangeArrow />
    <ValueChip color={afterColor}>{after}</ValueChip>
  </Stack>
);

const ReasonDetails = ({ reason }: { reason: Record<string, unknown> | null }) => {
  if (!reason) return null;
  return (
    <>
      <Box sx={{ width: '2px', bgcolor: 'divider', alignSelf: 'stretch' }} />

      {Boolean(reason.type) && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Label>Type:</Label>
          <Chip
            size="small"
            variant="outlined"
            label={<Typography variant="body2">{String(reason.type)}</Typography>}
          />
        </Stack>
      )}

      {Boolean(reason.label) && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Label>Label:</Label>
          <Typography variant="body2" noWrap>
            {String(reason.label)}
          </Typography>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Label>Status:</Label>
        <Chip
          size="small"
          variant="filled"
          color={reason.is_active ? 'success' : 'default'}
          label={<Typography variant="body2">{reason.is_active ? 'Active' : 'Inactive'}</Typography>}
        />
      </Stack>
    </>
  );
};

const renderChanges = (changeType: string, changes: Record<string, unknown>): ReactNode => {
  switch (changeType) {
    case BpfChangeType.STAGE_COMPLETED: {
      const stage = changes.stage as BeforeAfter | undefined;
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Label>Stage:</Label>
          <ValueChip color="success">{getNameOrValue(stage?.after)}</ValueChip>
        </Stack>
      );
    }

    case BpfChangeType.STAGE_RENAMED: {
      const stage = changes.stage as BeforeAfter | undefined;
      return (
        <BeforeAfterRow label="Stage" before={getNameOrValue(stage?.before)} after={getNameOrValue(stage?.after)} />
      );
    }

    case BpfChangeType.TEMPLATE_RENAMED: {
      const name = changes.name as BeforeAfter | undefined;
      return <BeforeAfterRow label="Name" before={getNameOrValue(name?.before)} after={getNameOrValue(name?.after)} />;
    }

    case BpfChangeType.TEMPLATE_ACTIVATED: {
      const template = changes.template as BeforeAfter | undefined;
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Label>Template:</Label>
          <ValueChip color="success">{getNameOrValue(template?.after)}</ValueChip>
        </Stack>
      );
    }

    case BpfChangeType.TEMPLATE_DEACTIVATED: {
      const template = changes.template as BeforeAfter | undefined;
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Label>Template:</Label>
          <ValueChip color="error">{getNameOrValue(template?.after)}</ValueChip>
        </Stack>
      );
    }

    case BpfChangeType.TEMPLATE_DUPLICATED: {
      const template = changes.template as BeforeAfter | undefined;
      return (
        <BeforeAfterRow
          label="Source"
          before={getNameOrValue(template?.before)}
          after={getNameOrValue(template?.after)}
          afterColor="info"
        />
      );
    }

    case BpfChangeType.TEMPLATE_CREATED: {
      const template = changes.template as BeforeAfter | undefined;
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Label>Template</Label>
          <ValueChip color="success">{getNameOrValue(template?.after)}</ValueChip>
        </Stack>
      );
    }

    case BpfChangeType.RULE_CHANGED: {
      const applyRules = changes.applyRules as BeforeAfter | undefined;
      const before = applyRules?.before as Record<string, unknown> | null;
      const after = applyRules?.after as Record<string, unknown> | null;
      const fields = Object.keys(after ?? before ?? {});

      const formatEnumValue = (val: unknown): string => {
        const str = String(val ?? '—');
        return str
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      };

      const formatFieldName = (field: string): string =>
        field
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, c => c.toUpperCase())
          .trim();

      return (
        <Stack spacing={0.5}>
          {fields.map(field => (
            <BeforeAfterRow
              key={field}
              label={formatFieldName(field)}
              before={formatEnumValue(before?.[field])}
              after={formatEnumValue(after?.[field])}
              afterColor="warning"
            />
          ))}
        </Stack>
      );
    }

    case BpfChangeType.STAGE_NOTIFICATION_CHANGED: {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            size="small"
            color="info"
            variant="filled"
            label={
              <Typography variant="body2" fontWeight={500}>
                Notification rules updated
              </Typography>
            }
          />
        </Stack>
      );
    }

    case BpfChangeType.CLOSED_REASON_CREATED: {
      const closeReason = changes.closeReason as BeforeAfter | undefined;
      const after = closeReason?.after as Record<string, unknown> | null;
      return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
          <Chip size="small" color="success" variant="filled" label={<Typography>Created</Typography>} />
          <ReasonDetails reason={after} />
        </Stack>
      );
    }

    case BpfChangeType.CLOSED_REASON_REMOVED: {
      const closeReason = changes.closeReason as BeforeAfter | undefined;
      const before = closeReason?.before as Record<string, unknown> | null;
      return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
          <Chip size="small" color="error" variant="filled" label={<Typography>Removed</Typography>} />
          <ReasonDetails reason={before} />
        </Stack>
      );
    }

    case BpfChangeType.CLOSED_REASON_RENAMED: {
      const label = changes.label as BeforeAfter | undefined;
      const closeReason = changes.closeReason as BeforeAfter | undefined;
      const reasonAfter = closeReason?.after as Record<string, unknown> | null;
      const beforeVal = getNameOrValue(label?.before);
      const afterVal = getNameOrValue(label?.after);
      const isEmpty = (v: string) => v === '—' || v === '';

      return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
          {isEmpty(beforeVal) && !isEmpty(afterVal) ? (
            <>
              <Label>Label set to:</Label>
              <ValueChip color="primary">{afterVal}</ValueChip>
            </>
          ) : (
            <>
              <Label>Label:</Label>
              <ValueChip>{isEmpty(beforeVal) ? '(empty)' : beforeVal}</ValueChip>
              <ChangeArrow />
              <ValueChip color="primary">{isEmpty(afterVal) ? '(empty)' : afterVal}</ValueChip>
            </>
          )}
          <ReasonDetails reason={reasonAfter} />
        </Stack>
      );
    }

    case BpfChangeType.CLOSED_REASON_ACTIVATED: {
      const closeReason = changes.closeReason as BeforeAfter | undefined;
      const reasonAfter = closeReason?.after as Record<string, unknown> | null;
      return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
          <Label>Active:</Label>
          <ValueChip>No</ValueChip>
          <ChangeArrow />
          <ValueChip color="success">Yes</ValueChip>
          <ReasonDetails reason={reasonAfter} />
        </Stack>
      );
    }

    case BpfChangeType.CLOSED_REASON_DEACTIVATED: {
      const closeReason = changes.closeReason as BeforeAfter | undefined;
      const reasonAfter = closeReason?.after as Record<string, unknown> | null;
      return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
          <Label>Active:</Label>
          <ValueChip>Yes</ValueChip>
          <ChangeArrow />
          <ValueChip color="error">No</ValueChip>
          <ReasonDetails reason={reasonAfter} />
        </Stack>
      );
    }

    default:
      return (
        <Typography variant="body2" color="text.secondary">
          {JSON.stringify(changes)}
        </Typography>
      );
  }
};

interface ChangesCellProps {
  changeType: string;
  changes: Record<string, unknown>;
}

export const ChangesCell = ({ changeType, changes }: ChangesCellProps) => (
  <Box sx={{ py: 0.5 }}>{renderChanges(changeType, changes)}</Box>
);
